<template>
    <section v-if="slides.length" class="relative w-full select-none"
        @mouseenter="pause" @mouseleave="resume">
        <!-- Slider viewport -->
        <div class="relative h-[88vh] min-h-[520px] max-h-[820px] w-full overflow-hidden bg-gray-900">
            <!-- Slides -->
            <div v-for="(item, i) in slides" :key="item.id"
                class="absolute overflow-hidden inset-0 transition-opacity duration-[900ms] ease-out"
                :class="i === current ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'">
                <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title"
                    class="h-full w-full object-cover grayscale"
                    :class="i === current ? 'animate-kenburns' : ''" fetchpriority="high"/>
                <div v-else class="h-full w-full bg-gradient-to-br from-gray-800 to-gray-900"></div>
                <!-- Grey / brand tint overlay -->
                <div class="absolute inset-0 bg-gray-100/75 dark:bg-gray-900/55"></div>
                <div class="absolute inset-0 bg-gradient-to-r from-gray-200/80 dark:from-gray-900/80 via-body-900/40 to-body-900/20"></div>
                <div data-v-4a48c47f="" class="absolute inset-0 bg-brand-900/15"></div>
            </div>

            <!-- Caption -->
            <div class="relative z-20 mx-auto flex h-full max-w-7xl items-center px-5 sm:px-8 lg:px-10">
                <transition :name="'slide-cap'" mode="out-in">
                    <div :key="active.id" class="max-w-2xl pb-28 sm:pb-24">
                        <p class="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-black/80 dark:text-white/80">
                            <span class="text-brand-500">New</span>
                            <span v-if="monthOf(active.startDate)"> &nbsp;&ndash;&nbsp; {{ monthOf(active.startDate) }}</span>
                        </p>

                        <h2 class="text-4xl font-light leading-[1.12] text-black dark:text-white sm:text-5xl lg:text-6xl">
                            <span class="font-extrabold me-2 ">{{ leadWords(active.title) }}</span>
                            <template v-if="restWords(active.title)"> {{ restWords(active.title) }}</template>
                        </h2>

                        <p v-if="active.description"
                            class="mt-5 max-w-xl text-lg font-light leading-relaxed text-black/75 dark:text-white/75 line-clamp-2">
                            {{ active.description }}
                        </p>

                        <!-- Countdown to start -->
                        <div v-if="countdown && ((typeof active.hasDate !== 'boolean') || (typeof active.hasDate === 'boolean' && active.hasDate))" class="mt-7 flex items-start gap-5 sm:gap-7">
                            <div v-for="unit in countdown" :key="unit.key" class="flex flex-col items-center">
                                <span class="text-3xl font-extrabold leading-none tabular-nums text-black dark:text-white sm:text-4xl">
                                    {{ pad(unit.value) }}
                                </span>
                                <span class="mt-1.5 text-[10px] font-bold uppercase tracking-widest text-black/60 dark:text-white/60 sm:text-xs">
                                    {{ unit.label }}
                                </span>
                            </div>
                        </div>

                        <div class="mt-9 flex flex-wrap items-center gap-4">
                            <NuxtLink :to="registerTarget(active)"
                                class="inline-flex items-center gap-2 rounded-sm bg-brand-500 px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-on-primary shadow-lg transition-colors hover:bg-brand-600">
                                Register
                            </NuxtLink>
                            <NuxtLink :to="detailTarget(active)"
                                class="inline-flex items-center gap-2 rounded-sm border border-black/70 dark:border-white/70 px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-black dark:text-white transition-colors hover:bg-white hover:text-slate-1  00 dark:hover:text-slate-900">
                                Learn More
                            </NuxtLink>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Dot navigation (vertical, right) -->
            <div v-if="slides.length > 1"
                class="absolute right-5 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-3 sm:right-8">
                <button v-for="(item, i) in slides" :key="'dot-' + item.id" type="button"
                    :aria-label="`Go to slide ${i + 1}`" :aria-current="i === current"
                    class="h-3 w-3 transition-all duration-300"
                    :class="i === current ? 'bg-brand-500 scale-110' : 'bg-white/70 hover:bg-white'"
                    @click="goTo(i)"></button>
            </div>

            <!-- Prev / Next -->
            <template v-if="slides.length > 1">
                <button type="button" aria-label="Previous slide" @click="prev"
                    class="absolute left-3 top-1/2 z-20 hidden -translate-y-1/2 rounded-full border border-white/30 p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white sm:block">
                    <ChevronLeft class="h-6 w-6" />
                </button>
                <button type="button" aria-label="Next slide" @click="next"
                    class="absolute right-16 top-1/2 z-20 hidden -translate-y-1/2 rounded-full border border-white/30 p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white sm:block">
                    <ChevronRight class="h-6 w-6" />
                </button>
            </template>
        </div>

        <!-- Red date / time bar -->
        <div class="relative z-20 -mt-16 bg-brand-500 text-on-primary sm:-mt-14">
            <div class="mx-auto flex max-w-4xl items-center justify-center gap-4 px-6 py-6 sm:py-7">
                <div class="text-right leading-tight md:min-w-64">
                    <p class="text-md font-light sm:text-xl">{{ dayMonthOf(event!.startDate) }}</p>
                    <p class="text-3xl font-extrabold sm:text-4xl">{{ yearOf(event!.startDate) }}</p>
                </div>

                <!-- Clock badge -->
                <div class="relative -my-16 flex h-32 w-32 shrink-0 items-center justify-center rounded-full bg-brand-500 sm:-my-24 sm:h-48 sm:w-48">
                    <div class="flex h-full w-full items-center justify-center rounded-full border-0 border-primary-500 sm:border-8">
                        <Clock class="h-28 w-28 text-on-primary sm:h-52 sm:w-52" :stroke-width="1.5" />
                    </div>
                </div>

                <div class="leading-tight md:min-w-64">
                    <p class="text-md font-light sm:text-xl">Events Time</p>
                    <p class="text-3xl font-extrabold sm:text-4xl">{{ timeOf(event!.startDate) }}</p>
                </div>
            </div>
        </div>
        <div class="h-5"></div>
    </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { EventItem } from '~/types';
import { sampleEvents } from './data';

const props = withDefaults(defineProps<{
    /** Single event to build the slider from (uses its imageUrl + optional images[]). */
    event?: EventItem | null;
    /** Explicit list of slides; overrides `event` when provided. */
    items?: EventItem[];
    interval?: number;
}>(), {
    event: null,
    items: () => [],
    interval: 6000,
});

const slides = computed<EventItem[]>(() => {
    if (props.items && props.items.length) return props.items;

    const ev = props.event;
    if (ev) {
        const list = [...(ev.eventSlides || [])].sort(
            (a, b) => (a.position ?? 0) - (b.position ?? 0)
        );
        if (list.length) {
            return list.map((s, i) => ({
                ...ev,
                id: s.id || `${ev.id}-slide-${i}`,
                title: s.title || ev.title,
                description: s.description || ev.description,
                imageUrl: s.imageUrl || ev.imageUrl,
                startDate: s.date || ev.startDate,
                hasDate: typeof s.hasDate === 'boolean' ? s.hasDate : ev.hasDate,
                link: s.link || null,
                position: s.position ?? i,
            }));
        }
        return [ev];
    }

    return sampleEvents;
});

const current = ref(0);
const active = computed(() => slides.value[current.value] || slides.value[0]);

let timer: ReturnType<typeof setInterval> | null = null;
const paused = ref(false);

// --- live countdown to the active slide's start date ---
const now = ref(useRenderTime());
let ticker: ReturnType<typeof setInterval> | null = null;
const pad = (n: number) => String(n).padStart(2, '0');

const countdown = computed(() => {
    const raw = active.value?.startDate ? new Date(active.value.startDate) : null;
    if (!raw || isNaN(raw.getTime())) return null;
    const total = Math.max(0, Math.floor((raw.getTime() - now.value) / 1000));
    return [
        { key: 'd', label: 'Days', value: Math.floor(total / 86400) },
        { key: 'h', label: 'Hours', value: Math.floor((total % 86400) / 3600) },
        { key: 'm', label: 'Minutes', value: Math.floor((total % 3600) / 60) },
        { key: 's', label: 'Seconds', value: total % 60 },
    ];
});

const goTo = (i: number) => {
    current.value = (i + slides.value.length) % slides.value.length;
};
const next = () => goTo(current.value + 1);
const prev = () => goTo(current.value - 1);

const start = () => {
    stop();
    if (slides.value.length > 1 && props.interval > 0) {
        timer = setInterval(() => {
            if (!paused.value) next();
        }, props.interval);
    }
};
const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
};
const pause = () => { paused.value = true; };
const resume = () => { paused.value = false; };

const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') prev();
    else if (e.key === 'ArrowRight') next();
};

onMounted(() => {
    start();
    now.value = Date.now();
    ticker = setInterval(() => { now.value = Date.now(); }, 1000);
    window.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
    stop();
    if (ticker) clearInterval(ticker);
    ticker = null;
    window.removeEventListener('keydown', onKey);
});
watch(() => slides.value.length, () => {
    if (current.value >= slides.value.length) current.value = 0;
    start();
});

// --- date helpers ---
const toDate = (iso?: string) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
};
const monthOf = (iso?: string) => {
    const d = toDate(iso);
    return d ? d.toLocaleString('default', { month: 'long' }).toUpperCase() : '';
};
const dayMonthOf = (iso?: string) => {
    const d = toDate(iso);
    return d ? d.toLocaleDateString('default', { day: 'numeric', month: 'long' }) : 'Date TBA';
};
const yearOf = (iso?: string) => {
    const d = toDate(iso);
    return d ? String(d.getFullYear()) : '—';
};
const timeOf = (iso?: string) => {
    const d = toDate(iso);
    return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '--:--';
};

// --- title split: first two words bold, remainder light ---
const leadWords = (title: string) => (title || '').split(' ').slice(0, 2).join(' ');
const restWords = (title: string) => (title || '').split(' ').slice(2).join(' ');

// --- routing ---
const detailTarget = (item: EventItem) => ({
    name: 'EventSingle',
    params: { slug: item.slug || item.id },
});
const registerTarget = (item: EventItem) => ({
    name: 'EventSingle',
    params: { slug: item.slug || item.id },
    query: { section: 'tickets' },
});
</script>

<style scoped>
/* Ken Burns zoom on the active slide image */
@keyframes kenburns {
    from {
        transform: scale(1);
    }

    to {
        transform: scale(1.08);
    }
}

.animate-kenburns {
    animation: kenburns 7s ease-out forwards;
}

/* Caption transition */
.slide-cap-enter-active {
    transition: opacity 0.6s ease, transform 0.6s ease;
}

.slide-cap-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-cap-enter-from {
    opacity: 0;
    transform: translateY(18px);
}

.slide-cap-leave-to {
    opacity: 0;
    transform: translateY(-12px);
}

@media (prefers-reduced-motion: reduce) {

    .animate-kenburns,
    .slide-cap-enter-active,
    .slide-cap-leave-active {
        animation: none;
        transition: none;
    }
}
</style>
